import React, { useState } from 'react';
import { Dialog, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import House from '../../Assets/images/house.png';

function UnitSelectorModal({ open, handleClose, property, onContinue }) {
  const [units, setUnits] = useState(2); // Default to 2 based on image

  if (!property) return null;

  // from unitPrice
  const priceRaw = property.unitPrice ? Number(property.unitPrice) : 0;

  const totalValue = units * priceRaw;

  // Format as currency
  const formattedTotal = new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(totalValue);
  const formattedPricePerUnit = new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(priceRaw);

  const quickSelects = [1, 2, 4, 10];

  const handleIncrement = () => {
    if (units < (property.unitsLeft || 12)) {
      setUnits(prev => prev + 1);
    }
  };

  const handleDecrement = () => {
    if (units > 1) {
      setUnits(prev => prev - 1);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '24px',
          padding: '24px',
          backgroundColor: '#FAFAFB',
          boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <IconButton onClick={handleClose} size="small">
            <ArrowBackIcon fontSize="small" style={{ color: '#1A1A1B' }} />
          </IconButton>
          <span className="font-bold text-[14px] text-back-color2">Back</span>
        </div>
        <div
          onClick={handleClose}
          className="w-8 h-8 rounded-full bg-[#1A1A1B] flex items-center justify-center cursor-pointer hover:bg-gray-800"
        >
          <CloseIcon fontSize="small" style={{ color: 'white' }} />
        </div>
      </div>

      {/* Property Summary Card */}
      <div className="bg-white rounded-2xl p-3 flex gap-4 items-center mb-6 shadow-sm border border-gray-100">
        <div className="w-[80px] h-[80px] rounded-xl overflow-hidden flex-shrink-0">
          <img src={property.image || House} alt={property.title} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1">
          <div className="flex justify-between items-start mb-1">
            <h3 className="font-bold text-[14px] text-back-color2 leading-tight">{property.title}</h3>
            <span className={`text-[10px] px-2 py-0.5 rounded text-white ${property.typeColor || 'bg-[#00BC67]'}`}>
              {property.type}
            </span>
          </div>
          <p className="text-[10px] text-gray-400 mb-3">{formattedPricePerUnit} per unit</p>
          <p className="text-[10px] text-gray-500">
            AVAILABLE : <span className="font-bold text-black">{property.unitsLeft} UNITS</span>
          </p>
        </div>
      </div>

      {/* Quick Select */}
      <div className="mb-6">
        <p className="text-[10px] text-gray-500 font-bold mb-3 tracking-wider">QUICK SELECT</p>
        <div className="flex gap-2 justify-between">
          {quickSelects.map(num => (
            <button
              key={num}
              onClick={() => setUnits(num)}
              className={`flex-1 py-2 rounded-full text-[12px] font-bold transition-colors ${units === num
                  ? 'bg-[#E6F8F0] text-primary-color border border-primary-color'
                  : 'bg-[#F3F4F6] text-gray-600'
                }`}
            >
              {num} {num === 1 ? 'unit' : 'units'}
            </button>
          ))}
        </div>
      </div>

      {/* Custom Select */}
      <div className="mb-6">
        <p className="text-[10px] text-gray-500 font-bold mb-3 tracking-wider">CUSTOM</p>
        <div className="bg-white rounded-full flex justify-between items-center p-1.5 border border-gray-100">
          <button
            onClick={handleDecrement}
            className="w-10 h-10 rounded-full bg-[#A8E6CF] flex items-center justify-center text-white hover:bg-[#85e0c0] transition-colors"
          >
            <RemoveIcon fontSize="small" />
          </button>
          <span className="font-bold text-[16px] text-back-color2">{units}</span>
          <button
            onClick={handleIncrement}
            className="w-10 h-10 rounded-full bg-[#00BC67] flex items-center justify-center text-white hover:bg-[#00a359] transition-colors"
          >
            <AddIcon fontSize="small" />
          </button>
        </div>
        <p className="text-center text-[10px] text-gray-400 mt-2">
          of {property.unitsLeft || null} available units
        </p>
      </div>

      {/* Totals */}
      <div className="bg-white rounded-2xl p-4 mb-6 shadow-sm border border-gray-100">
        <div className="flex justify-between items-center mb-3 pb-3 border-b border-gray-100">
          <span className="text-[12px] text-gray-400">Price Per Unit</span>
          <span className="font-bold text-[14px] text-back-color2">{formattedPricePerUnit}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[12px] text-gray-400">Total Value</span>
          <span className="font-bold text-[16px] text-back-color2">{formattedTotal}</span>
        </div>
      </div>

      {/* Continue Button */}
      <button
        onClick={() => onContinue({ units, totalValue, formattedTotal })}
        className="w-full bg-primary-color text-white font-bold py-3.5 rounded-xl text-[14px] hover:bg-[#00a359] transition-colors"
      >
        Continue - {formattedTotal}
      </button>

    </Dialog>
  );
}

export default UnitSelectorModal;
